import { useEffect, useState } from "react";

import axiosInstance from "../../api/axiosConfig";

export type PagePreview =
  | { status: "loading"; url: null }
  | { status: "ready"; url: string }
  | { status: "unavailable"; url: null };

/**
 * One object URL per (case, node, version, page), shared by every hover card
 * for the session. A node's id survives a PDF edit, so the file's checksum is
 * part of the key (and the request, to get past the server's day of cache);
 * within one version, pages never change, so nothing here ever expires.
 */
const cache = new Map<string, Promise<string | null>>();

const fetchPage = (
  individualId: string,
  nodeId: string,
  pageIndex: number,
  version: string | null
): Promise<string | null> => {
  const key = `${individualId}/${nodeId}/${version ?? ""}/${pageIndex}`;
  let pending = cache.get(key);
  if (!pending) {
    pending = axiosInstance
      .get(`/fs/${individualId}/node/${nodeId}/thumbnail`, {
        responseType: "blob",
        params: version ? { page: pageIndex, v: version } : { page: pageIndex }
      })
      .then((response) => URL.createObjectURL(response.data as Blob))
      .catch(() => null);
    cache.set(key, pending);
  }
  return pending;
};

/** Test seam: forget every cached page. */
export const clearPagePreviewCache = () => cache.clear();

/**
 * The PNG of one page of a case file for the citation hover card. `page` is
 * 1-based (a pincite); the first page is shown when the citation has none.
 * `version` (the node's checksum) refreshes the preview after a PDF edit.
 */
export const usePagePreview = (
  individualId: string | null | undefined,
  nodeId: string | null | undefined,
  page: number | null | undefined,
  version?: string | null
): PagePreview => {
  const pageIndex = Math.max(0, (page ?? 1) - 1);
  const versionKey = version ?? null;
  const [state, setState] = useState<PagePreview>({
    status: "loading",
    url: null
  });

  useEffect(() => {
    if (!individualId || !nodeId) {
      setState({ status: "unavailable", url: null });
      return undefined;
    }
    let cancelled = false;
    setState({ status: "loading", url: null });
    fetchPage(individualId, nodeId, pageIndex, versionKey).then((url) => {
      if (cancelled) return;
      setState(
        url ? { status: "ready", url } : { status: "unavailable", url: null }
      );
    });
    return () => {
      cancelled = true;
    };
  }, [individualId, nodeId, pageIndex, versionKey]);

  return state;
};
